import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  CreditCard,
  Coffee,
  ListOrdered,
  Users,
  LogOut,
  Globe,
  Tag,
  Activity,
  Settings,
  CalendarDays,
  List,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import apiClient from "../service/ApiClient";
import { getRestaurantId } from "../utils/JwtUtil";
import { getRestaurantById } from "../service/restaurant";
import { S3_BASE_URL } from "../utils/Constants";

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurantName, setRestaurantName] = useState("Foody");
  const [restaurantData, setRestaurantData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      i18n.language === "ar" ? "rtl" : "ltr"
    );
  }, [i18n.language]);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  const toggleLang = () => {
    const nextLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(nextLang);
    localStorage.setItem("language", nextLang);
    apiClient.defaults.headers["Accept-Language"] = nextLang;
    window.location.reload();
  };

  useEffect(() => {
    const id = getRestaurantId();
    if (id) {
      getRestaurantById(id, i18n.language)
        .then((data) => {
          console.log(data);
          setRestaurantName(data.name);
          setRestaurantData(data);
        })
        .catch((err) => console.error("Failed to load restaurant:", err));
    }
  }, [i18n.language]);

  const navigationItems = [
    {
      path: "/home",
      icon: Home,
      label: t("dashboard.title"),
      group: "main",
    },
    {
      path: "/customer-meals",
      icon: List,
      label: t("customer-meals.title"),
      group: "main",
    },
    {
      path: "/customers",
      icon: Users,
      label: t("customers.navTitle"),
      group: "main",
    },
    {
      path: "/subscriptions",
      icon: CreditCard,
      label: t("subscriptions"),
      group: "content",
    },
    {
      path: "/categories",
      icon: Tag,
      label: t("categories"),
      group: "content",
    },
    {
      path: "/meals",
      icon: Coffee,
      label: t("meals"),
      group: "content",
    },
    {
      path: "/meals-availability",
      icon: CalendarDays,
      label: t("mealAvailability.navTitle"),
      group: "content",
    },
    {
      path: "/menu-jobs",
      icon: Activity,
      label: t("menuJobs"),
      group: "content",
    },
    {
      path: "/image-sliders",
      icon: ListOrdered,
      label: t("imageSliders"),
      group: "content",
    },
    {
      path: "/settings",
      icon: Settings,
      label: t("settings.title"),
      group: "admin",
    },
  ];

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const NavLink = ({ item }) => {
    const Icon = item.icon;
    const isActive = isActiveLink(item.path);

    return (
      <Link
        to={item.path}
        className={`group flex items-center rounded-xl transition-all duration-200 ease-in-out ${
          isCollapsed ? "px-3 py-3 justify-center" : "px-3 py-3"
        } ${
          isActive
            ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25"
            : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
        }`}
        onClick={() => setIsSidebarOpen(false)}
        title={isCollapsed ? item.label : ""}
      >
        <Icon
          size={20}
          className={`transition-all duration-200 ${
            isCollapsed ? "" : i18n.language === "ar" ? "ml-3" : "mr-3"
          } ${
            isActive ? "text-white" : "text-gray-500 group-hover:text-blue-600"
          }`}
        />
        {!isCollapsed && <span className="font-medium">{item.label}</span>}
      </Link>
    );
  };

  const groupedItems = navigationItems.reduce((groups, item) => {
    if (!groups[item.group]) {
      groups[item.group] = [];
    }
    groups[item.group].push(item);
    return groups;
  }, {});

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Mobile backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
        fixed lg:static inset-y-0 left-0 z-50
        ${isCollapsed ? "w-20" : "w-72"} bg-white border-r border-gray-200 
        transform transition-all duration-300 ease-in-out
        ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }
        flex flex-col shadow-xl lg:shadow-none
      `}
      >
        {/* Header */}
        <div
          className={`flex items-center border-b border-gray-100 ${
            isCollapsed ? "justify-center p-4" : "justify-between p-6"
          }`}
        >
          {!isCollapsed ? (
            <Link to="/home" className={`flex items-center ${i18n.language === "ar" ? "space-x-4 space-x-reverse" : "space-x-4"}`}>
              <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
                {restaurantData?.imageUrl ? (
                  <img
                    src={S3_BASE_URL + "/" + restaurantData.imageUrl}
                    alt={restaurantName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                ) : null}
                <Coffee
                  className={`w-6 h-6 text-white ${
                    restaurantData?.imageUrl ? "hidden" : "block"
                  }`}
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 truncate max-w-40">
                  {restaurantName}
                </h1>
                <p className="text-sm text-gray-500">Admin Panel</p>
              </div>
            </Link>
          ) : (
            <Link
              to="/home"
              className="flex items-center justify-center"
              title={restaurantName}
            >
              <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
                {restaurantData?.imageUrl ? (
                  <img
                    src={S3_BASE_URL + "/" + restaurantData.imageUrl}
                    alt={restaurantName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                ) : null}
                <Coffee
                  className={`w-6 h-6 text-white ${
                    restaurantData?.imageUrl ? "hidden" : "block"
                  }`}
                />
              </div>
            </Link>
          )}

          {/* Desktop Toggle Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={20} />
            ) : (
              <ChevronLeft size={20} />
            )}
          </button>

          {/* Mobile Close Button */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className={`flex-1 overflow-y-auto ${
            isCollapsed ? "px-2 py-4" : "px-4 py-6"
          }`}
        >
          {/* Main Section */}
          <div className="mb-8">
            {!isCollapsed && (
              <h2 className="px-3 mb-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t("quickLinks")}
              </h2>
            )}
            <div className="space-y-2">
              {groupedItems.main?.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </div>
          </div>

          {/* Content Management */}
          <div className="mb-8">
            {!isCollapsed && (
              <h2 className="px-3 mb-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Content Management
              </h2>
            )}
            <div className="space-y-2">
              {groupedItems.content?.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </div>
          </div>

          {/* Admin Section */}
          <div>
            {!isCollapsed && (
              <h2 className="px-3 mb-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Administration
              </h2>
            )}
            <div className="space-y-2">
              {groupedItems.admin?.map((item) => (
                <NavLink key={item.path} item={item} />
              ))}
            </div>
          </div>
        </nav>

        {/* Footer Actions */}
        <div
          className={`border-t border-gray-100 space-y-2 ${
            isCollapsed ? "p-2" : "p-4"
          }`}
        >
          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className={`w-full flex items-center text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-xl transition-all duration-200 ${
              isCollapsed ? "px-3 py-3 justify-center" : "px-3 py-3"
            }`}
            title={
              isCollapsed
                ? i18n.language === "en"
                  ? "العربية"
                  : "English"
                : ""
            }
          >
            <Globe
              size={20}
              className={`text-gray-500 ${
                isCollapsed ? "" : i18n.language === "ar" ? "ml-3" : "mr-3"
              }`}
            />
            {!isCollapsed && (
              <span className="font-medium">
                {i18n.language === "en" ? "العربية" : "English"}
              </span>
            )}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 ${
              isCollapsed ? "px-3 py-3 justify-center" : "px-3 py-3"
            }`}
            title={isCollapsed ? t("logout") : ""}
          >
            <LogOut
              size={20}
              className={
                isCollapsed ? "" : i18n.language === "ar" ? "ml-3" : "mr-3"
              }
            />
            {!isCollapsed && <span className="font-medium">{t("logout")}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {restaurantName}
            </h1>
            <div className="w-10 h-10">
              {" "}
              {/* Placeholder for balance */}
              <ThemeToggle />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-full">{children}</div>
        </main>
      </div>
    </div>
  );
}
